import React, { useState, useEffect, useRef } from 'react';
import { getUserProfile, updateUserProfile, updatePassword, normalizeMobileNumber } from '../services/userService';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DashboardLayout from '../components/layout/DashboardLayout';

// Default avatar placeholder as data URI
const DEFAULT_AVATAR = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23CCCCCC"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';

interface UserProfileData {
  _id: string;
  name: string;
  lastName: string;
  nationalId: string;
  mobile: string;
  email: string;
  birthdate: string;
  gender: string;
  avatar: string | null;
  createdAt: string;
  updatedAt: string;
  organizations: Array<{
    organization: {
      _id: string;
      name: string;
    };
    role: string;
  }>;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserProfileData | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [previewAvatar, setPreviewAvatar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Check for tab query parameter on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    
    if (tabParam === 'security') {
      setActiveTab('password');
    }
  }, [location.search]);

  // Form data states
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    nationalId: '',
    mobile: '',
    email: '',
    birthdate: '',
    gender: '',
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    profile: {
      name: '',
      lastName: '',
      nationalId: '',
      mobile: '',
      email: '',
      gender: '',
    },
    password: {
      password: '',
      confirmPassword: '',
      match: '',
    },
  });

  // Load user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const response = await getUserProfile();
        if (response.success && response.data) {
          const user = response.data;
          
          // Format date for input field (YYYY-MM-DD)
          let formattedDate = '';
          if (user.birthdate) {
            formattedDate = new Date(user.birthdate).toISOString().split('T')[0];
          }
          
          setUserData(user);
          setFormData({
            name: user.name || '',
            lastName: user.lastName || '',
            nationalId: user.nationalId || '',
            mobile: user.mobile || '',
            email: user.email || '',
            birthdate: formattedDate,
            gender: user.gender || '',
          });
          
          // Set avatar preview
          if (user.avatar) {
            setPreviewAvatar(user.avatar);
          }
        }
      } catch (error) {

        toast.error('خطا در دریافت اطلاعات کاربر');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Update the URL when tab changes, without page reload
  const handleTabChange = (tab: 'profile' | 'password') => {
    setActiveTab(tab);
    
    // Update URL without reloading the page
    const newUrl = tab === 'password' 
      ? `${location.pathname}?tab=security` 
      : location.pathname;
    
    window.history.replaceState({}, '', newUrl);
  };

  // Handle input changes for profile form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Clear any previous errors for this field
    if (errors.profile[name as keyof typeof errors.profile]) {
      setErrors(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [name]: '',
        }
      }));
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle input changes for password form
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Clear any previous errors for this field
    if (errors.password[name as keyof typeof errors.password]) {
      setErrors(prev => ({
        ...prev,
        password: {
          ...prev.password,
          [name]: '',
          ...(name === 'password' || name === 'confirmPassword' ? { match: '' } : {}),
        }
      }));
    }
    
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle avatar selection
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('حجم تصویر باید کمتر از 5 مگابایت باشد');
        return;
      }
      
      // Check file type
      if (!file.type.match('image.*')) {
        toast.error('فقط فایل های تصویری مجاز هستند');
        return;
      }
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        if (e.target && typeof e.target.result === 'string') {
          setPreviewAvatar(e.target.result);
        }
      };
      
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click
  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Validate profile form
  const validateProfileForm = () => {
    let isValid = true;
    const newErrors = { ...errors };
    
    // Reset errors
    newErrors.profile = {
      name: '',
      lastName: '',
      nationalId: '',
      mobile: '',
      email: '',
      gender: '',
    };
    
    // Check required fields
    if (!formData.name.trim()) {
      newErrors.profile.name = 'نام الزامی است';
      isValid = false;
    }
    
    if (!formData.lastName.trim()) {
      newErrors.profile.lastName = 'نام خانوادگی الزامی است';
      isValid = false;
    }
    
    // Validate national ID (code melli) if provided
    if (formData.nationalId && formData.nationalId.trim()) {
      // National ID should be 10 digits
      if (!/^\d{10}$/.test(formData.nationalId.trim())) {
        newErrors.profile.nationalId = 'کد ملی باید 10 رقم باشد';
        isValid = false;
      }
    }
    
    // Validate mobile number if provided
    if (formData.mobile && !/^(0|98|\+98)9[0-9]{9}$/.test(normalizeMobileNumber(formData.mobile))) {
      newErrors.profile.mobile = 'شماره موبایل نامعتبر است';
      isValid = false;
    }
    
    // Validate email if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.profile.email = 'ایمیل نامعتبر است';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  // Validate password form
  const validatePasswordForm = () => {
    let isValid = true;
    const newErrors = { ...errors };
    
    // Reset errors
    newErrors.password = {
      password: '',
      confirmPassword: '',
      match: '',
    };
    
    // Password length
    if (!passwordData.password) {
      newErrors.password.password = 'رمز عبور الزامی است';
      isValid = false;
    } else if (passwordData.password.length < 6) {
      newErrors.password.password = 'رمز عبور باید حداقل 6 کاراکتر باشد';
      isValid = false;
    }
    
    // Confirm password
    if (!passwordData.confirmPassword) {
      newErrors.password.confirmPassword = 'تکرار رمز عبور الزامی است';
      isValid = false;
    }
    
    // Check if passwords match
    if (passwordData.password !== passwordData.confirmPassword) {
      newErrors.password.match = 'رمز عبور و تکرار آن مطابقت ندارند';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  // Handle profile update submission
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Create FormData for avatar and other fields
      const profileData = new FormData();
      
      // Add regular fields
      profileData.append('name', formData.name);
      profileData.append('lastName', formData.lastName);
      profileData.append('nationalId', formData.nationalId);
      profileData.append('email', formData.email);
      profileData.append('birthdate', formData.birthdate);
      profileData.append('gender', formData.gender);
      
      // Add avatar if changed
      if (previewAvatar && previewAvatar !== userData?.avatar && !previewAvatar.startsWith('http')) {
        // Extract base64 data from the DataURL
        const base64Data = previewAvatar.split(',')[1];
        if (base64Data) {
          // Convert base64 to Blob
          const blob = await (await fetch(`data:image/png;base64,${base64Data}`)).blob();
          profileData.append('avatar', blob, 'avatar.png');
        }
      }
      
      const response = await updateUserProfile(profileData);
      
      if (response.success) {
        toast.success('اطلاعات پروفایل با موفقیت به‌روزرسانی شد');
      } else {
        toast.error(response.message || 'خطا در به‌روزرسانی اطلاعات');
      }
    } catch (error: any) {

      // Handle specific error responses
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 400) {
        toast.error('اطلاعات وارد شده نامعتبر است');
      } else if (error.response?.status === 409) {
        toast.error('کد ملی قبلاً توسط کاربر دیگری استفاده شده است');
      } else {
        toast.error('خطا در به‌روزرسانی اطلاعات پروفایل');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle password update submission
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      setIsChangingPassword(true);
      
      const response = await updatePassword({
        password: passwordData.password,
        confirmPassword: passwordData.confirmPassword
      });
      
      if (response.success) {
        toast.success('رمز عبور با موفقیت به‌روزرسانی شد');
        
        // Reset password fields
        setPasswordData({
          password: '',
          confirmPassword: '',
        });
      } else {
        toast.error(response.message || 'خطا در به‌روزرسانی رمز عبور');
      }
    } catch (error) {

      toast.error('خطا در به‌روزرسانی رمز عبور');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="bg-slate-100 min-h-screen">
        {/* Tabs Header */}
        <div className="pt-4 bg-white border-b border-slate-200">
          <div className="container mx-auto px-4">
            <nav className="flex border-b border-slate-200">
              <button
                onClick={() => handleTabChange('profile')}
                className={`inline-block py-2 px-3 text-sm font-medium cursor-pointer border-b-2 ${
                  activeTab === 'profile'
                    ? 'text-blue-600 border-blue-600'
                    : 'border-transparent text-slate-600 hover:text-blue-600'
                }`}
              >
                اطلاعات هویتی
              </button>
              <button
                onClick={() => handleTabChange('password')}
                className={`inline-block py-2 px-3 text-sm font-medium cursor-pointer border-b-2 ${
                  activeTab === 'password'
                    ? 'text-blue-600 border-blue-600'
                    : 'border-transparent text-slate-600 hover:text-blue-600'
                }`}
              >
                تنظیمات امنیتی
              </button>
            </nav>
          </div>
        </div>
      
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="px-4 pb-4 mt-4">
            <form onSubmit={handleProfileSubmit} className='bg-white p-4 rounded-md border-1 border-slate-200'>
              {/* Avatar Upload */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative mb-4">
                  <img
                    src={previewAvatar || DEFAULT_AVATAR}
                    alt="تصویر حساب"
                    className="w-32 h-32 rounded-full object-cover border-4 border-slate-200"
                  />
                  <button
                    type="button"
                    onClick={handleAvatarClick}
                    className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full p-2 shadow-lg hover:bg-blue-600 transition"
                    title="تغییر تصویر حساب"
                    aria-label="تغییر تصویر حساب"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                    </svg>
                  </button>
                  <input 
                    type="file"
                    id="avatar"
                    name="avatar"
                    ref={fileInputRef}
                    onChange={handleAvatarChange}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
                <p className="text-sm text-slate-500">
                  برای آپلود تصویر کلیک کنید (حداکثر 5 مگابایت)
                </p>
              </div>

              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    نام
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900"
                  />
                  {errors.profile.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.profile.name}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    نام خانوادگی
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900"
                  />
                  {errors.profile.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.profile.lastName}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    کد ملی
                  </label>
                  <input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900"
                    dir="ltr"
                    maxLength={10}
                  />
                  {errors.profile.nationalId && (
                    <p className="mt-1 text-sm text-red-600">{errors.profile.nationalId}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    شماره موبایل
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900 bg-slate-100"
                    dir="ltr"
                    disabled
                  />
                  {errors.profile.mobile && (
                    <p className="mt-1 text-sm text-red-600">{errors.profile.mobile}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ایمیل
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900"
                    dir="ltr"
                  />
                  {errors.profile.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.profile.email}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    تاریخ تولد
                  </label>
                  <input
                    type="date"
                    name="birthdate"
                    value={formData.birthdate}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900"
                    dir="ltr"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    جنسیت
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center space-x-2 space-x-reverse cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={formData.gender === 'male'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-slate-700">مرد</span>
                    </label>
                    <label className="flex items-center space-x-2 space-x-reverse cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={formData.gender === 'female'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-slate-700">زن</span>
                    </label>
                  </div>
                  {errors.profile.gender && (
                    <p className="mt-1 text-sm text-red-600">{errors.profile.gender}</p>
                  )}
                </div>
              </div>
              
              {/* Position field removed as requested */}
              
              <div className="text-left">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="cursor-pointer py-2 px-6 text-sm bg-blue-500 rounded-sm text-white flex items-center gap-2 border-1 border-blue-500"
                >
                  {isSaving ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      در حال ذخیره...
                    </span>
                  ) : (
                    'ذخیره تغییرات'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="px-4 pb-4 mt-4">
            <form onSubmit={handlePasswordSubmit} className='bg-white p-4 rounded-md border-1 border-slate-200'>
              <div className='w-1/2'>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    رمز عبور جدید
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={passwordData.password}
                    onChange={handlePasswordChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900"
                    dir="ltr"
                  />
                  {errors.password.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.password}</p>
                  )}
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    تکرار رمز عبور جدید
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900"
                    dir="ltr"
                  />
                  {errors.password.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.confirmPassword}</p>
                  )}
                  {errors.password.match && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.match}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="cursor-pointer py-2 px-6 text-sm bg-blue-500 rounded-sm text-white flex items-center gap-2 border-1 border-blue-500"
                >
                  {isChangingPassword ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      در حال ذخیره...
                    </span>
                  ) : (
                    'ذخیره رمز عبور'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Profile;
